import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Edit2, Trash2, Eye, EyeOff, Tag, X } from 'lucide-react';
import { tenantApi } from '../../lib/tenant-api';
import { RichTextEditor } from '../rich-text-editor';
import type { FollowUpQuestionItemProps } from './types';
import { MAX_DEPTH } from './types';

export const FollowUpQuestionItem: React.FC<FollowUpQuestionItemProps> = React.memo(({
  question,
  level = 1,
  parentNumber = '1',
  index = 0,
  visitedPath = new Set<string>(),
  allQuestions = [],
  onDisplayFollowUps,
  onManageCTAs,
  onAddFollowUp,
  onEdit,
  onToggleActive,
  onDelete,
  onRemoveCTA,
  getFollowUpQuestions,
  questionCTAs,
  displayFollowUps,
  availableLabels,
  onCreateLabel,
  onAssignLabels,
  getCTATypeColor,
  fetchQuestions
}) => {
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showAssignLabelDropdown, setShowAssignLabelDropdown] = useState(false);
  const [showAssignTypeDropdown, setShowAssignTypeDropdown] = useState(false);
  const [showCreateLabelInput, setShowCreateLabelInput] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showOverflowMenu && !showAssignLabelDropdown && !showAssignTypeDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target as Node)) {
        setShowOverflowMenu(false);
        setShowAssignLabelDropdown(false);
        setShowAssignTypeDropdown(false);
        setShowCreateLabelInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOverflowMenu, showAssignLabelDropdown, showAssignTypeDropdown]);

  if (level >= MAX_DEPTH) {
    return (
      <div className="bg-red-50 border-2 border-red-300  p-4">
        <div className="text-sm font-medium text-red-800">
          ⚠️ Maximum depth reached ({MAX_DEPTH} levels)
        </div>
      </div>
    );
  }

  if (visitedPath.has(question.id)) {
    return (
      <div className="bg-orange-50 border-2 border-orange-300  p-4">
        <div className="text-sm font-medium text-orange-800">
          🔄 Circular reference detected
        </div>
      </div>
    );
  }

  const followUps = getFollowUpQuestions(question.id);
  const hasFollowUps = followUps.length > 0;
  const assignedCTAs = questionCTAs[question.id] || [];
  const areFollowUpsVisible = displayFollowUps.has(question.id);
  const newVisitedPath = new Set([...visitedPath, question.id]);
  const hierarchicalNumber = `${parentNumber}.${index + 1}`;
  const marginClass = level === 1 ? '' : 'ml-3 sm:ml-8';
  const borderColorClass = level === 1
    ? 'border-l-4 border-l-purple-500'
    : level === 2
      ? 'border-l-4 border-l-pink-500'
      : 'border-l-4 border-l-pink-400';

  return (
    <div className={`mb-3 ${marginClass}`}>
      <div className={`bg-white border shadow-sm p-4 ${borderColorClass}`}>
        <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
          Follow up question
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-white font-semibold text-xs ${level === 1 ? 'border border-purple-200 text-purple-600' : 'border border-pink-200 text-pink-600'}`}>
              {hierarchicalNumber}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 text-sm">{question.question}</h4>
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2">
            {hasFollowUps && (
              <button
                onClick={() => onDisplayFollowUps(question.id)}
                className={`px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1 ${areFollowUpsVisible ? (level === 1 ? 'border-b-2 border-b-purple-500' : 'border-b-2 border-b-pink-500') : ''}`}
              >
                <span>Display follow-up</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${areFollowUpsVisible ? 'rotate-180' : ''}`} />
              </button>
            )}
            <button onClick={() => onManageCTAs(question)} className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1">
              <span>⊕</span><span>Assign CTAs</span>
            </button>
            <button onClick={() => onAddFollowUp(question)} className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200 flex items-center gap-1">
              <span>↪</span><span>Assign Follow-ups</span>
            </button>

            <div className="relative" ref={overflowMenuRef}>
              <button onClick={() => setShowOverflowMenu(!showOverflowMenu)} className="p-2 py-1 text-gray-700 bg-gray-100  hover:bg-gray-200">•••</button>

              {showOverflowMenu && !showAssignLabelDropdown && !showAssignTypeDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200  shadow-lg z-10">
                  <button onClick={() => { setShowOverflowMenu(false); setShowAssignLabelDropdown(true); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Tag className="h-4 w-4" /><span>Assign Label</span>
                  </button>
                  <button onClick={() => { setShowOverflowMenu(false); setShowAssignTypeDropdown(true); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Tag className="h-4 w-4" /><span>Assign Type</span>
                  </button>
                  <button onClick={() => { setShowOverflowMenu(false); onEdit(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Edit2 className="h-4 w-4" /><span>Edit</span>
                  </button>
                  <button onClick={() => { setShowOverflowMenu(false); onToggleActive(question); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    {question.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    <span>{question.is_active ? 'Deactivate' : 'Activate'}</span>
                  </button>
                  <button onClick={() => { setShowOverflowMenu(false); onDelete(question.id); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <Trash2 className="h-4 w-4" /><span>Delete</span>
                  </button>
                </div>
              )}

              {showAssignLabelDropdown && (
                <div className="absolute right-full mr-1 top-0 w-52 bg-white border border-gray-200  shadow-lg z-10">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm text-gray-800 font-medium">Assign Label</div>
                      <button onClick={() => setShowAssignLabelDropdown(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                    </div>
                    {!showCreateLabelInput ? (
                      <button onClick={() => setShowCreateLabelInput(true)} className="w-full text-left px-2 py-1.5 text-indigo-600 hover:bg-gray-50  flex items-center gap-2 mb-2">
                        <Plus className="h-3.5 w-3.5" /><span className="text-sm font-medium">Create Label</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-1 mb-2">
                        <input type="text" value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)} placeholder="Label name" className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300  text-sm" autoFocus />
                        <button onClick={async () => { if (newLabelName.trim()) { await onCreateLabel(newLabelName); setNewLabelName(''); setShowCreateLabelInput(false); } }} className="flex-shrink-0 px-2 py-1.5 bg-indigo-600 text-white text-xs font-medium  hover:bg-indigo-700">Add</button>
                        <button onClick={() => setShowCreateLabelInput(false)} className="flex-shrink-0 text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {availableLabels.map((lbl) => {
                        const currentLabelIds = (question.labels || []).map(l => l.id);
                        return (
                          <label key={lbl.id} className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={currentLabelIds.includes(lbl.id)} onChange={async () => {
                              const newIds = currentLabelIds.includes(lbl.id) ? currentLabelIds.filter(id => id !== lbl.id) : [...currentLabelIds, lbl.id];
                              await onAssignLabels(question.id, newIds);
                            }} className="h-4 w-4 text-indigo-600 border-gray-300 " />
                            <span className="text-sm text-gray-700">{lbl.name}</span>
                          </label>
                        );
                      })}
                      {availableLabels.length === 0 && <p className="text-xs text-gray-400">No labels yet. Create one above.</p>}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button onClick={() => setShowAssignLabelDropdown(false)} className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Done</button>
                    </div>
                  </div>
                </div>
              )}

              {showAssignTypeDropdown && (
                <div className="absolute right-full mr-1 top-0 w-52 bg-white border border-gray-200  shadow-lg z-10">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm text-gray-800 font-medium">Assign Type</div>
                      <button onClick={() => setShowAssignTypeDropdown(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="space-y-2">
                      {(['is_root', 'is_faq', 'is_conversion'] as const).map(field => (
                        <label key={field} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={question[field]} onChange={async () => {
                            try { await tenantApi.updatePrimerQuestion(question.id, { [field]: !question[field] }); await fetchQuestions(); } catch (err) { console.error(err); }
                          }} className="h-4 w-4 text-indigo-600 border-gray-300 " />
                          <span className="text-sm text-gray-700">{field === 'is_root' ? 'Root Question' : field === 'is_faq' ? 'FAQ Question' : 'Conversion Question'}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button onClick={() => setShowAssignTypeDropdown(false)} className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium  hover:bg-indigo-700">Done</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="bg-gray-50  border border-gray-200">
            <RichTextEditor value={question.answer} onChange={() => { }} hideToolbar defaultPreviewMode height="500px" minHeight="150px" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {question.is_root && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Root question</span>}
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${question.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
            {question.is_active ? 'Active' : 'Inactive'}
          </span>
          {hasFollowUps && <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">{followUps.length} follow-up{followUps.length !== 1 ? 's' : ''}</span>}
        </div>

        {assignedCTAs.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-gray-600 mb-2">Assigned CTAs:</div>
            <div className="flex flex-wrap gap-2">
              {assignedCTAs.map((cta) => (
                <div key={cta.id} className="flex items-center gap-2 px-3 py-1 bg-gray-50  border border-gray-200">
                  <span className={`px-2 py-0.5  text-xs font-medium ${getCTATypeColor(cta.cta_type)}`}>
                    {cta.cta_type === 'link' ? 'Link' : cta.cta_type === 'generic' ? 'Generic' : cta.cta_type}
                  </span>
                  <span className="text-sm">{cta.text}</span>
                  <button onClick={() => onRemoveCTA(question.id, cta.id)} className="text-gray-400 hover:text-red-600">×</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {areFollowUpsVisible && hasFollowUps && (
        <div className="mt-3 space-y-3">
          {followUps.map((followUp, idx) => (
            <FollowUpQuestionItem
              key={followUp.id}
              question={followUp}
              level={level + 1}
              parentNumber={hierarchicalNumber}
              index={idx}
              visitedPath={newVisitedPath}
              allQuestions={allQuestions}
              onDisplayFollowUps={onDisplayFollowUps}
              onManageCTAs={onManageCTAs}
              onAddFollowUp={onAddFollowUp}
              onEdit={onEdit}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
              onRemoveCTA={onRemoveCTA}
              getFollowUpQuestions={getFollowUpQuestions}
              questionCTAs={questionCTAs}
              displayFollowUps={displayFollowUps}
              availableLabels={availableLabels}
              onCreateLabel={onCreateLabel}
              onAssignLabels={onAssignLabels}
              getCTATypeColor={getCTATypeColor}
              fetchQuestions={fetchQuestions}
            />
          ))}
        </div>
      )}
    </div>
  );
});

FollowUpQuestionItem.displayName = 'FollowUpQuestionItem';
